import * as React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { filterBy } from "@progress/kendo-data-query";

import ErrorBanner from "../../components/ErrorBanner";
import { useQueryGetClientsMasterList } from "../../data/services/clientAdminService";
import { useMutatePostFeatureAccessClients } from "../../data/services/featureAccessService";

const ALL_CLIENTS_ID = "@All";

// Adapted from pages/workflow/Panels/OEWorkflowAddClientsModel.jsx.
const FeatureAccessAddClientsModal = ({
  oePeriodId,
  featureKey,
  onClose,
  onSubmit,
}) => {
  const [submitted, setSubmitted] = React.useState(false);
  const [clients, setClients] = React.useState([]);
  const [clientsDataSource, setClientsDataSource] = React.useState([]);
  const [selectedValues, setSelectedValues] = React.useState([]);

  const { isLoading: availableClientsLoading, data: availableClients } =
    useQueryGetClientsMasterList();
  const addClientsHandler = useMutatePostFeatureAccessClients();

  React.useEffect(() => {
    if (availableClientsLoading || !availableClients?.data) {
      return;
    }

    const mapped = availableClients.data.map((client) => ({
      ...client,
      clientDispName: `${client.clientId} - ${client.clientName}`,
    }));

    if (mapped.length === 0) {
      setClients([]);
      setClientsDataSource([]);
      return;
    }

    const withAll = [
      { clientId: ALL_CLIENTS_ID, clientDispName: "All Clients" },
      ...mapped,
    ];

    setClients(withAll);
    setClientsDataSource(withAll.slice());
  }, [availableClientsLoading, availableClients]);

  const handleChange = (event) => {
    const currentSelectAll = selectedValues.some(
      (item) => item.clientId === ALL_CLIENTS_ID
    );
    const nextSelectAll = event.value.some(
      (item) => item.clientId === ALL_CLIENTS_ID
    );
    let value = event.value;
    const currentCount = selectedValues.length;
    const nextCount = value.length;

    if (
      nextCount > currentCount &&
      !currentSelectAll &&
      !nextSelectAll &&
      clients.length - 1 === nextCount
    ) {
      value = clients;
    } else if (
      nextCount < currentCount &&
      currentCount === clients.length &&
      currentSelectAll &&
      nextSelectAll
    ) {
      value = value.filter((item) => item.clientId !== ALL_CLIENTS_ID);
    } else if (!currentSelectAll && nextSelectAll) {
      value = clients;
    } else if (currentSelectAll && !nextSelectAll) {
      value = [];
    }

    setSelectedValues(value);
  };

  const handleSubmit = async () => {
    const clientIds = selectedValues
      .map((item) => item.clientId)
      .filter((clientId) => clientId !== ALL_CLIENTS_ID);

    if (clientIds.length === 0) {
      return;
    }

    setSubmitted(true);

    try {
      await addClientsHandler.mutateAsync({
        oePeriodId,
        // One entry per client, all granting the feature selected on the page.
        // A client needing several features is several entries; the import
        // dialog is the way to do that in bulk.
        entries: clientIds.map((clientId) => ({ clientId, featureKey })),
      });
      setSubmitted(false);
      onSubmit();
    } catch {
      setSubmitted(false);
    }
  };

  const itemRender = (li, itemProps) => {
    const itemChildren = (
      <span>
        <input
          type="checkbox"
          name={itemProps.dataItem}
          checked={itemProps.selected}
          onChange={(e) => itemProps.onClick(itemProps.index, e)}
        />
        &nbsp;{li.props.children}
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  const filterChange = (event) => {
    setClientsDataSource(filterBy(clients.slice(), event.filter));
  };

  const selectedCount = selectedValues.filter(
    (item) => item.clientId !== ALL_CLIENTS_ID
  ).length;

  return (
    <Dialog title={"Add clients to feature"} onClose={onClose} width={600}>
      <ErrorBanner
        handler={addClientsHandler}
        onError={() => setSubmitted(false)}
      />
      <p>
        Select the clients that should see this feature. Clients already on the
        list are left unchanged.
      </p>
      <MultiSelect
        data={clientsDataSource}
        itemRender={itemRender}
        textField={"clientDispName"}
        dataItemKey={"clientId"}
        autoClose={false}
        value={selectedValues}
        onChange={handleChange}
        filterable={true}
        onFilterChange={filterChange}
        tags={
          selectedCount > 0
            ? [
                {
                  text: `${selectedCount} clients selected`,
                  data: [...selectedValues],
                },
              ]
            : []
        }
      />
      <div style={{ paddingTop: 20 }}></div>
      <DialogActionsBar layout={"end"}>
        <Button onClick={onClose}>{"Cancel"}</Button>
        <Button
          type={"submit"}
          themeColor={"success"}
          disabled={submitted || selectedCount === 0}
          onClick={handleSubmit}
        >
          {"Add Clients"}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default FeatureAccessAddClientsModal;
